import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getPCard } from 'utils/apiRequests';
import {
	defaultColDefination,
	getMonthYearRange,
	initialGridData,
} from 'CategoryManagement/Explore/DetailedAnalysis/utils';
import { formatNumber } from 'utils/number';
import { getRequiredCustomFilterObjectFormat } from 'CategoryManagement/utils';

const IncreasedMonthlyPCardContext = createContext();

const monthsText = 'months with';
const increasetText = 'increase';
const columnDefs = [
	{ headerName: 'Category', field: 'category' },
	{ headerName: 'AP L4Category', field: 'aP_L4Category' },
	{ headerName: 'Period', field: 'period' },
	{
		headerName: 'Percentage Growth',
		field: 'momGrowth',
		valueFormatter: params => {
			console.log(params);
			if (params.value !== '-') {
				return `${parseFloat(params.value).toFixed(1)}%`;
			} else {
				return params.value;
			}
		},
	},
	{
		headerName: 'Threshold',
		field: 'change',
		valueFormatter: params => {
			return `${parseFloat(params.value).toFixed(1)}%`;
		},
	},
];

export const IncreasedMonthlyPCardProvider = ({ children }) => {
	const { isSaving, pCardCategories, loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const [isLoading, setIsLoading] = useState(false);
	const [analysis, setAnalysis] = useState(
		`X ${monthsText} ${pCardCategories}% ${increasetText}`
	);
	const [gridData, setGridData] = useState(initialGridData);

	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSaving && !isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getPCard(payload)
						.then(response => {
							if (response.succeeded) {
								if (response.data === null) {
									const analysisString = `0 ${monthsText} ${pCardCategories}% ${increasetText}`;
									setAnalysis(analysisString);
									setGridData(prevState => ({
										...prevState,
										rowData: [],
										columnDefs: [],
										defaultColDef: defaultColDefination,
									}));
									setIsLoading(false);
								} else {
									if (!!Object.keys(response.data).length) {
										const monthCount = response.data.count;
										const analysisString = `${formatNumber(
											monthCount
										)} ${monthsText} ${pCardCategories}% ${increasetText}`;
										setAnalysis(analysisString);
										if (response.data.explore.length > 0) {
											const rowResult = response.data.explore.map(el => {
												const inputDate = {
													currentYear: el.currentYear,
													currentMonth: el.currentMonth,
												};
												const { current, previous } = getMonthYearRange(
													inputDate
												);
												return {
													...el,
													period: `${previous} - ${current}`,
													change: pCardCategories,
													momGrowth: el.momGrowth === null ? '-' : el.momGrowth,
												};
											});
											const colResult = columnDefs;
											setGridData(prevState => ({
												...prevState,
												rowData: rowResult,
												columnDefs: colResult,
												defaultColDef: defaultColDefination,
											}));
										}
										setIsLoading(false);
									}
								}
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			isSaving,
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			customFilters,
			customFiltersValues,
		]
	);

	return (
		<IncreasedMonthlyPCardContext.Provider
			value={{ isLoading, analysis, gridData }}
		>
			{children}
		</IncreasedMonthlyPCardContext.Provider>
	);
};

export const useIncreasedMonthlyPCardContext = () => {
	return useContext(IncreasedMonthlyPCardContext);
};
